import React, { useCallback } from 'react';
import { Plus, MessageSquare, Trash2, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { NyxtLogoText } from './NyxtLogo';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

// Header component
const SidebarHeader = () => (
  <div className="p-4 border-b" style={{ borderColor: 'rgba(220, 215, 206, 0.5)' }}>
    <NyxtLogoText />
  </div>
);

// New chat button component
const NewChatButton = ({ onClick }) => (
  <div className="p-4">
    <Button
      onClick={onClick}
      data-testid="new-chat-btn"
      className="
        w-full justify-start gap-3 
        bg-white hover:bg-[#F9F8F6]
        text-[#1A1D1A] hover:text-[#1A1D1A]
        border border-[#DCD7CE]
        rounded-xl py-5
        transition-all duration-200
        btn-lift
      "
      variant="outline"
    >
      <Plus className="w-4 h-4" />
      <span className="font-medium">New chat</span>
    </Button>
  </div>
);

// Conversation item component
const ConversationItem = ({ conversation, isActive, onSelect, onDelete }) => {
  const handleDelete = useCallback((e) => {
    e.stopPropagation();
    onDelete(conversation.conversation_id);
  }, [conversation.conversation_id, onDelete]);

  return (
    <div
      className={`
        group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer
        transition-all duration-200
        ${isActive 
          ? 'bg-[#DCD7CE]/70 text-[#1A1D1A]' 
          : 'text-[#596059] hover:bg-[#DCD7CE]/40 hover:text-[#1A1D1A]'
        }
      `}
      onClick={() => onSelect(conversation.conversation_id)}
      data-testid="chat-history-item"
    >
      <MessageSquare className="w-4 h-4 flex-shrink-0" />
      <span className="flex-1 truncate text-sm">{conversation.title}</span>
      <button
        onClick={handleDelete}
        className="
          opacity-0 group-hover:opacity-100 
          p-1 rounded hover:bg-[#DCD7CE]
          transition-opacity duration-200
        "
        data-testid="delete-chat-btn"
      >
        <Trash2 className="w-3.5 h-3.5 text-[#8A908A] hover:text-[#D97757]" />
      </button>
    </div>
  );
};

// Conversations list component
const ConversationsList = ({ conversations, currentConversationId, onSelect, onDelete }) => (
  <ScrollArea className="flex-1 px-4">
    <div className="space-y-1 pb-4">
      {conversations.map((conv) => (
        <ConversationItem
          key={conv.conversation_id}
          conversation={conv}
          isActive={currentConversationId === conv.conversation_id}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      ))}
      
      {conversations.length === 0 && (
        <div className="text-center py-8 text-[#8A908A] text-sm">
          No conversations yet
        </div>
      )}
    </div>
  </ScrollArea>
);

// User profile component
const UserProfile = ({ user, onLogout }) => (
  <div className="p-4 border-t" style={{ borderColor: 'rgba(220, 215, 206, 0.5)' }}>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button 
          className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#DCD7CE]/50 transition-colors"
          data-testid="user-profile-menu"
        >
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback style={{ backgroundColor: '#D97757', color: '#F9F8F6' }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-[#1A1D1A] truncate">
              {user?.name || 'Guest'}
            </p>
            <p className="text-xs text-[#8A908A] truncate">
              {user?.email || 'Guest user'}
            </p>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem disabled>
          <User className="w-4 h-4 mr-2" />
          {user?.is_guest ? 'Guest Account' : 'My Account'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={onLogout}
          className="text-red-600 focus:text-red-600"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
);

export const Sidebar = ({ 
  conversations, 
  currentConversationId, 
  onSelectConversation, 
  onNewChat, 
  onDeleteConversation,
  user,
  onLogout
}) => {
  return (
    <div 
      className="w-72 flex-shrink-0 flex flex-col h-full"
      style={{ 
        backgroundColor: '#EBE8E1',
        borderRight: '1px solid rgba(220, 215, 206, 0.5)'
      }}
    >
      <SidebarHeader />
      <NewChatButton onClick={onNewChat} />
      <ConversationsList 
        conversations={conversations}
        currentConversationId={currentConversationId}
        onSelect={onSelectConversation}
        onDelete={onDeleteConversation}
      />
      <UserProfile user={user} onLogout={onLogout} />
    </div>
  );
};

export default Sidebar;
